import { isNil, snakeCase } from 'lodash';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum, addThousandsSeparator } from 'utils/helper/common';
import { GRAPH_COLORS } from 'utils/graphs/utils';
import { insertIf } from 'utils/list';

import type {
  LineGraphDataZoom,
  LineGraphGrid,
  LineGraphLegend,
  LineGraphTitle,
  LineGraphTitleStyles,
  LineGraphTitleStylesCommon,
  LineGraphType,
  LineGraphXAxis,
  LineGraphYAxis
} from 'types';

type StringNumber = string | number;

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY: string = 'sov_shared';

export const getColorOptions = (): Array<string> => GRAPH_COLORS;

export const getGridOptions = (): LineGraphGrid => ({
  width: '87%',
  right: '5%',
  bottom: 110,
  top: 40,
  left: '8%'
});

export const getXAxisOptions = (data: Array<string>): LineGraphXAxis => ({
  type: 'category',
  data,
  boundaryGap: true,

  nameTextStyle: {
    color: '#3a3a3a'
  },

  axisTick: {
    alignWithLabel: true,
    length: 8
  },

  axisLine: {
    onZero: false,
    lineStyle: {
      color: '#e0e0e0'
    }
  },

  axisLabel: {
    fontSize: 14,
    color: '#3a3a3a',
    margin: 10,
    // @ts-ignore
    formatter: undefined
  }
});

export const getYAxisLabelFormatted = (value: number, min: number, max: number, isSingleQuery: boolean): StringNumber | undefined | null => {
  if (value !== min && value !== max) {
    if (isSingleQuery) {
      return value < 1000 ? String(Math.round(value)) : abbrNum(value);
    }

    return `${Math.round(value)}%`;
  }

  return null;
};

export const getYAxisOptions = (min: number, max: number, isSingleQuery: boolean, showWeekLabel: boolean): LineGraphYAxis => ({
  type: 'value',
  name: showWeekLabel ? __t(`${SHARED_MSG_KEY}.labels.week`) : '',
  min,
  max,
  nameLocation: 'start',
  nameGap: 10,

  nameTextStyle: {
    fontFamily: 'Hind',
    fontSize: 14,
    color: '#3a3a3a'
  },

  interval: isSingleQuery ? max / 10 : 10,

  axisTick: {
    alignWithLabel: true,
    length: 8
  },

  axisLine: {
    lineStyle: {
      color: '#e0e0e0'
    }
  },

  axisLabel: {
    formatter: value => getYAxisLabelFormatted(value, min, max, isSingleQuery),
    fontSize: 14,
    color: '#3a3a3a',
    margin: 10
  },

  splitLine: {
    show: true,
    lineStyle: {
      color: '#f1f3f5',
      type: 'solid'
    }
  }
});

type TitleFormatText = {
  type: 'note' | 'value',
  text: string
};

type TitleFormatLineBreak = {
  type: 'line-break'
};

type TitleData = Array<TitleFormatText | TitleFormatLineBreak>;

export const getTitleFormatterValue = (data: TitleData): string => data.map((entry) => {
  if (entry.type === 'note' || entry.type === 'value') {
    return `{${entry.type}|${entry.text}}`;
  }

  if (entry.type === 'line-break') {
    return '\n';
  }

  return '';
}).join('');

export const getTitleOptionsStyles = (): LineGraphTitleStyles => {
  const styles: LineGraphTitleStylesCommon = {
    fontFamily: 'Hind',
    fontSize: 12,
    lineHeight: 18,
    align: 'left',
    color: '#3a3a3a'
  };

  return {
    note: {
      ...styles
    },
    value: {
      ...styles,
      fontWeight: 600
    }
  };
};

export const getTitleOptions = (dataType: string, graphType: LineGraphType, uniqueMentions?: number | null): LineGraphTitle => {
  const translatedGraphType = __t(`${COMMON_MSG_KEY}.social.${graphType}`);
  const titleKey = `${COMMON_MSG_KEY}.kpis.${dataType === 'posts' && graphType === 'youtube'
    ? 'videos_posted'
    : snakeCase(dataType)}`;

  const data: TitleData = [
    {
      type: 'note',
      text: __t(`${SHARED_MSG_KEY}.labels.development_over_time`)
    },
    {
      type: 'value',
      text: `${__t(titleKey)} (${translatedGraphType})`
    },
    ...insertIf(!isNil(uniqueMentions),
      {
        type: 'line-break'
      },
      {
        type: 'note',
        text: __t(`${SHARED_MSG_KEY}.messages.unique_mentions_limited`)
      },
      {
        type: 'value',
        text: addThousandsSeparator(uniqueMentions ?? 0)
      }
    )
  ];

  return {
    text: getTitleFormatterValue(data),
    bottom: 0,
    left: 0,
    right: 0,
    itemGap: 0,
    textStyle: {
      rich: getTitleOptionsStyles()
    }
  };
};

export const getDataZoomOptions = (isSingleQuery: boolean): Array<LineGraphDataZoom> => [
  {
    type: 'inside',
    rangeMode: ['percent', 'percent'],
    start: 0,
    end: 100,
    minSpan: 50,
    maxSpan: 100,
    xAxisIndex: [0, ...(!isSingleQuery ? [1] : [])]
  }
];

export const getLegendOptions = (): LineGraphLegend => ({
  type: 'scroll',
  orient: 'horizontal',
  width: '75%',
  bottom: 55,
  icon: 'roundRect',
  itemWidth: 13,
  itemHeight: 3,
  itemGap: 16,
  inactiveColor: '#c5c5c5',

  textStyle: {
    fontFamily: 'Hind',
    fontSize: 13,
    padding: [3, 0, 0, 4]
  }
});
